import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Auth } from 'aws-amplify';
import { Project, UserGroup, ProjectsFilters } from 'src/app/model';
import { ApiService } from 'src/app/providers/api/api.service';
import { sortByName } from 'src/app/app.utils';
import { HelperProvider } from '../providers';

interface OwnersState { loading: boolean; data: UserGroup[]; }
interface ProjectsState { loading: boolean; data: Project[]; }

@Injectable({
  providedIn: 'root',
})
export class UmStoreService {
  constructor(
    private api: ApiService,
    private helper: HelperProvider,
  ) { }

  /**
   * OWNERS
   */
  private readonly OWNERS = new BehaviorSubject<OwnersState>({ loading: true, data: [] });
  readonly owners$ = this.OWNERS.asObservable();

  public readonly selectedOwner = new BehaviorSubject<UserGroup | undefined>(undefined);
  readonly selectedOwner$: Observable<UserGroup | undefined> = this.selectedOwner.asObservable();

  get owners(): UserGroup[] {
    return this.OWNERS.getValue().data;
  }
  set owners(data: UserGroup[]) {
    this.OWNERS.next({ data, loading: this.ownersLoading });
  }
  get ownersLoading(): boolean {
    return this.OWNERS.getValue().loading;
  }
  set ownersLoading(loading: boolean) {
    this.OWNERS.next({ data: this.owners, loading });
  }

  /**
   * PROJECTS
   */
  private readonly PROJECTS = new BehaviorSubject<ProjectsState>({ loading: false, data: [] });
  readonly projects$ = this.PROJECTS.asObservable();

  public readonly selectedProject = new BehaviorSubject<Project | undefined>(undefined);
  readonly selectedProject$: Observable<Project | undefined> = this.selectedProject.asObservable();

  get projects(): Project[] {
    return this.PROJECTS.getValue().data;
  }
  set projects(data: Project[]) {
    this.PROJECTS.next({ data, loading: this.ownersLoading });
  }
  get projectsLoading(): boolean {
    return this.PROJECTS.getValue().loading;
  }
  set projectsLoading(loading: boolean) {
    this.PROJECTS.next({ data: this.projects, loading });
  }


  /**
   * additional methods
   */
  public selectProject(id?: number) {
    this.selectedProject.next(this.projects.find(project => project.id === id));
  }

  public selectOwner(id?: number) {
    this.selectProject();
    this.projects = [];
    if (id) {
      this.fetchProjects({ owner_id: id });
    }
    this.selectedOwner.next(this.owners.find(owner => owner.id === id));
  }

  public async fetchOwners(id: number) {
    this.selectProject();
    this.projects = [];
    this.ownersLoading = true;
    this.projectsLoading = true;
    const { groups } = await this.api.users.getUsersGroups(this.helper);
    this.ownersLoading = false;
    this.projectsLoading = false;

    if (groups && groups.length) {
      this.owners = groups.sort(sortByName);
      this.selectOwner(id);
    } else {
      this.owners = [];
    }
  }

  public async fetchProjects(filters?: ProjectsFilters, preselectedZone?: number, dontSetDefault?: boolean) {
    this.projectsLoading = true;
    this.ownersLoading = false;
    const { projects, message } = await this.api.users.projects(filters);
    this.projectsLoading = false;

    if (projects && projects.length) {
      this.projects = projects.sort(sortByName);

      if (preselectedZone) {
        const zone = await this.api.zones.getZoneById(preselectedZone);

        if (zone) {
          this.selectProject(zone.project_id);
        }
        return;
      }

      if (!dontSetDefault) {
        const defaultProject = projects.find((p) => p.is_default);
        if (defaultProject) {
          this.selectProject(defaultProject.id);
        }
      }
    } else {
      this.projects = [];

      if (message) {
        this.helper.alert(message, 'Getting projects error');
      }
    }
  }

  public clearListsAndSelectedData() {
    this.selectedProject.next(undefined);
    this.projects = [];
  }

  public async deleteCurrentUser(): Promise<boolean> {
    const userId = (await Auth.currentUserInfo())?.attributes?.sub;

    if (!userId) {
      this.helper.alert('Can\'t get user data');
      return false;
    }

    return await this.api.users.deleteUser(userId, this.helper) || false;
  }
}
